import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timer, Subscription } from 'rxjs';
import { cryptoJSNamespaceKey, getWindowRef } from '@hiptraveler/common';

export interface AuthEncryptedReq {
  /** Base64 AES ciphertext (CryptoJS default) */
  lr: string;
  /** hex-encoded IV (16 bytes) */
  iv: string;
  /** Base64 wrapped AES key (echo from /enc-sym-key) */
  ek: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthCryptoService {

  // in-memory only; never persist!
  private aesKeyBase64: string | null = null; // "ak" from server
  private wrappedKeyBase64: string | null = null; // "ek" from server
  private keyExpiryAt = 0;
  private expiryTimerSub?: Subscription;

  // tune to your risk/appetite (shorter = safer)
  private readonly keyTtlMs = 2 * 60 * 1000; // 2 minutes

  constructor(
    private http: HttpClient
  ) { }

  /** Public: ensure keys are present & fresh before encrypting/decrypting. */
  async ensureKeys(): Promise<void> {
    if (!this.hasValidKeys()) {
      await this.fetchKeys();
    }
  }

  /** Public: clear keys (call on logout/tab close/route change as needed). */
  clearKeys(): void {
    this.aesKeyBase64 = null;
    this.wrappedKeyBase64 = null;
    this.keyExpiryAt = 0;
    this.expiryTimerSub?.unsubscribe();
    this.expiryTimerSub = undefined;
  }

  /** Public: encrypt any JSON-serializable payload for POSTing to backend. */
  encryptPayload<T extends object>(payload: T): AuthEncryptedReq {
    if (!this.aesKeyBase64 || !this.wrappedKeyBase64) {
      throw new Error('Encryption key not loaded. Call ensureKeys() first.');
    }

    const CryptoJS = getWindowRef()[cryptoJSNamespaceKey];

    // Parse key + generate IV (16 bytes)
    const keyWord = CryptoJS.enc.Base64.parse(this.aesKeyBase64);
    const ivWord = CryptoJS.lib.WordArray.random(16);

    // AES-CBC + PKCS7 (CryptoJS default)
    const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(payload), keyWord, {
      iv: ivWord,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }).toString(); // Base64

    return {
      lr: ciphertext,
      iv: ivWord.toString(CryptoJS.enc.Hex),
      ek: this.wrappedKeyBase64 // echo back to server
    };
  }

  /** Public: decrypt a `{ data, iv }` payload from backend into object. */
  decryptResponse<T = any>(dataBase64: string, ivHex: string): T {
    if (!this.aesKeyBase64) {
      throw new Error('Decryption key not loaded. Call ensureKeys() first.');
    }

    const CryptoJS = getWindowRef()[cryptoJSNamespaceKey];

    const keyWord = CryptoJS.enc.Base64.parse(this.aesKeyBase64);
    const ivWord = CryptoJS.enc.Hex.parse(ivHex);

    const plainWord = CryptoJS.AES.decrypt(dataBase64, keyWord, {
      iv: ivWord,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const json = plainWord.toString(CryptoJS.enc.Utf8);
    if (!json) throw new Error('Failed to decrypt: empty plaintext.');
    return JSON.parse(json) as T;
  }

  /** Optional helper: lets callers check if key is present & fresh. */
  hasValidKeys(): boolean {
    return !!this.aesKeyBase64 && !!this.wrappedKeyBase64 && Date.now() < this.keyExpiryAt;
  }

  // ---- internals ----

  private async fetchKeys(): Promise<void> {
    // assumes backend returns: { ak: string(base64 AES), ek: string(base64 wrapped AES) }
    const { ak, ek } = await firstValueFrom(
      this.http.get<{ ak: string; ek: string }>('/api/auth/enc-sym-key')
    );

    if (!ak || !ek) throw new Error('enc-sym-key response invalid (missing ak/ek).');
    this.aesKeyBase64 = ak;
    this.wrappedKeyBase64 = ek;

    // set/refresh TTL
    this.keyExpiryAt = Date.now() + this.keyTtlMs;
    this.expiryTimerSub?.unsubscribe();
    this.expiryTimerSub = timer(this.keyTtlMs).subscribe(() => this.clearKeys());
  }

}
